// ── theme.js ─────────────────────────────────────────────────────────────
// Single source of truth: colors, currency, shared styles, dark mode.
// C uses raw hex values so C.accent + '18' works in inline styles.
// Theme mode: 'light' | 'dark' — user-selected only, stored in localStorage.
// The OS `prefers-color-scheme` preference is deliberately ignored so the
// UI never flips unexpectedly when the user's phone is in auto/night mode.
//
// Palette: "Tidewater" — cool Liquid Glass system.
//   Light: pearl canvas (#F5F6F3) with frosted glass cards floating over a
//          mint + sage + powder-blue aurora backdrop.
//   Dark:  deep teal canvas (#081614) with saturated mint/seafoam glow blobs
//          and translucent 6%-white glass cards.
// No warm-pink aurora tones anywhere. The app is fully sans-serif (Outfit);
// the `serif` token is retained as an alias so existing heading callsites
// keep working, but it now resolves to the same Outfit stack as `sans`.
// ──────────────────────────────────────────────────────────────────────────

// ── Palettes ─────────────────────────────────────────────────────────────
const LIGHT = {
  // Canvas & surfaces
  bg: '#F5F6F3', card: '#FFFFFF', surface: '#ECF0EC', border: '#E0E5DD',
  // Ink
  text: '#082A20', muted: '#5A6861', subtle: '#8B9A93',
  // Brand
  accent: '#0F6E56', accentMid: '#2ECC9B', accentLight: '#D5E8E0',
  accentSoft: 'rgba(15,110,86,0.08)',
  // Secondary accent kept for HR/Community pill compatibility
  gold: '#BA7517', goldSoft: 'rgba(186,117,23,0.08)',
  // Status & utility
  danger: '#D93025', purple: '#534AB7', blue: '#185FA5',
  dark: '#082A20', darkSoft: 'rgba(8,42,32,0.06)',
  // Liquid Glass — translucent tokens for frosted cards over the aurora
  glass: 'rgba(255,255,255,0.55)',
  glassStrong: 'rgba(255,255,255,0.72)',
  glassBorder: 'rgba(255,255,255,0.7)',
  glassShadow: '0 4px 20px rgba(0,0,0,0.04)',
  // Chrome — translucent canvas tones for fixed nav, footer, sticky CTAs.
  // These sit ABOVE glass cards and need to match the page canvas, not the
  // card surface, so they stay distinct from C.glass / C.glassStrong.
  chromeBg: 'rgba(246, 242, 232, 0.72)',
  chromeBgSoft: 'rgba(255, 255, 255, 0.28)',
  // Aurora — layered radial gradients painted on the page background
  aurora:
    'radial-gradient(at 28% 0%, #B8E5D1 0%, transparent 55%),' +
    'radial-gradient(at 85% 18%, #D5E8E0 0%, transparent 55%),' +
    'radial-gradient(at 15% 75%, #C5D8E8 0%, transparent 55%),' +
    'radial-gradient(at 75% 95%, #B8D4C8 0%, transparent 50%),' +
    '#F5F6F3',
}

const DARK = {
  bg: '#081614', card: '#101A18', surface: '#152422', border: '#1E2E2A',
  // Ink — `subtle` is deliberately bumped up (vs. #5A6E68 in older builds)
  // so legal footer links, timestamps, character counts, and captions
  // remain legible against the deep-teal canvas. Still visibly quieter
  // than `muted` but reads as real text, not dead pixels.
  text: '#E5EDE9', muted: '#A8B6B0', subtle: '#7E9088',
  accent: '#2ECC9B', accentMid: '#5AE0B8', accentLight: 'rgba(46,204,155,0.14)',
  accentSoft: 'rgba(46,204,155,0.08)',
  gold: '#E5A84B', goldSoft: 'rgba(229,168,75,0.1)',
  danger: '#EF5350', purple: '#8B7FE8', blue: '#5B9FEF',
  dark: '#E5EDE9', darkSoft: 'rgba(255,255,255,0.06)',
  glass: 'rgba(255,255,255,0.06)',
  glassStrong: 'rgba(255,255,255,0.10)',
  glassBorder: 'rgba(255,255,255,0.14)',
  glassShadow: '0 4px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.08)',
  // Chrome — deep-teal wash with more alpha so the nav/footer blur still
  // reads as "part of the page" rather than washed-out white.
  chromeBg: 'rgba(8, 22, 20, 0.72)',
  chromeBgSoft: 'rgba(8, 22, 20, 0.48)',
  aurora:
    'radial-gradient(at 28% 0%, rgba(46,204,155,0.30) 0%, transparent 55%),' +
    'radial-gradient(at 85% 15%, rgba(120,170,210,0.24) 0%, transparent 55%),' +
    'radial-gradient(at 15% 72%, rgba(90,180,175,0.22) 0%, transparent 55%),' +
    'radial-gradient(at 75% 95%, rgba(80,140,180,0.18) 0%, transparent 50%),' +
    '#081614',
}

// ── Theme mode management ────────────────────────────────────────────────
// Returns 'light' | 'dark'. Legacy 'auto' values (from earlier builds) are
// silently coerced to 'light' so existing users land on a deterministic
// mode instead of whatever their phone is currently rendering.
export function getThemeMode() {
  try {
    const saved = localStorage.getItem('settle-theme')
    if (saved === 'dark') return 'dark'
    return 'light' // 'light', missing, or legacy 'auto' → light
  } catch { return 'light' }
}

// Save preference — only 'light' | 'dark' are accepted.
export function setThemeMode(mode) {
  try { localStorage.setItem('settle-theme', mode === 'dark' ? 'dark' : 'light') } catch { /* ignore quota / privacy-mode errors */ }
}

// Resolve to actual light/dark. The OS preference is deliberately ignored —
// the user's explicit choice is the only signal.
export function resolveTheme(mode) {
  return mode === 'dark' ? 'dark' : 'light'
}

function isDark() {
  return resolveTheme(getThemeMode()) === 'dark'
}

// ── JS Color Tokens ──────────────────────────────────────────────────────
function buildTokens() {
  const dark = isDark()
  const p = dark ? DARK : LIGHT
  return {
    ...p,
    userBubble: p.accent,
    aiBubble: p.surface,
    cardR: 20, pill: 999, btnR: 16, inputR: 12,
    // Font families. The app is fully sans-serif; `serif` is kept as an
    // alias for heading callsites but resolves to the same Outfit stack.
    sans: "'Outfit', sans-serif",
    serif: "'Outfit', sans-serif",
    cardShadow: dark
      ? '0 2px 16px rgba(0,0,0,0.2), 0 0 1px rgba(0,0,0,0.3)'
      : '0 2px 16px rgba(0,0,0,0.04), 0 0 1px rgba(0,0,0,0.08)',
    heroShadow: dark
      ? '0 8px 40px rgba(0,0,0,0.4)'
      : '0 16px 36px -10px rgba(15,110,86,0.28)',
    navShadow: dark
      ? '0 8px 32px rgba(46,204,155,0.2), 0 2px 8px rgba(46,204,155,0.1)'
      : '0 8px 32px rgba(15,110,86,0.25), 0 2px 8px rgba(15,110,86,0.1)',
  }
}

export let C = buildTokens()

// Rebuild C and return new tokens (call after setThemeMode)
export function applyTheme() {
  Object.assign(C, buildTokens())
  return C
}

// (No system-preference listener — theme is user-selected only.)

// ── CSS Variables ────────────────────────────────────────────────────────
// Dark mode is opt-in via [data-theme="dark"] on <html>. The
// @media (prefers-color-scheme: dark) block has been removed so the OS
// night-mode setting never influences the app's appearance.
export const themeCSS = `
  :root {
    --bg: ${LIGHT.bg}; --card: ${LIGHT.card}; --surface: ${LIGHT.surface};
    --border: ${LIGHT.border}; --text: ${LIGHT.text}; --muted: ${LIGHT.muted};
    --accent: ${LIGHT.accent}; --accent-light: ${LIGHT.accentLight};
    --gold: ${LIGHT.gold}; --danger: ${LIGHT.danger};
    --purple: ${LIGHT.purple}; --blue: ${LIGHT.blue};
    --glass: ${LIGHT.glass}; --glass-border: ${LIGHT.glassBorder};
    color-scheme: light;
  }
  [data-theme="dark"] {
    --bg: ${DARK.bg}; --card: ${DARK.card}; --surface: ${DARK.surface};
    --border: ${DARK.border}; --text: ${DARK.text}; --muted: ${DARK.muted};
    --accent: ${DARK.accent}; --accent-light: ${DARK.accentLight};
    --gold: ${DARK.gold}; --danger: ${DARK.danger};
    --purple: ${DARK.purple}; --blue: ${DARK.blue};
    --glass: ${DARK.glass}; --glass-border: ${DARK.glassBorder};
    color-scheme: dark;
  }
`

// ── Raw hex for SVGs ─────────────────────────────────────────────────────
export const RAW = {
  accent: '#0F6E56', accentDark: '#2ECC9B',
  gold: '#BA7517', text: '#082A20', white: '#FFFFFF', dark: '#082A20',
}

// ── Currency (was duplicated 6×) ─────────────────────────────────────────
export const CURRENCY_MAP = {
  'Dubai': 'AED', 'Abu Dhabi': 'AED', 'Sharjah': 'AED', 'Ajman': 'AED',
  'Ras Al Khaimah': 'AED', 'Fujairah': 'AED', 'Al Ain': 'AED',
  'Riyadh': 'SAR', 'Jeddah': 'SAR', 'Dammam': 'SAR', 'Khobar': 'SAR',
  'Mecca': 'SAR', 'Medina': 'SAR', 'NEOM': 'SAR', 'KAEC': 'SAR',
  'Doha': 'QAR', 'Lusail': 'QAR', 'Al Wakrah': 'QAR', 'Al Khor': 'QAR',
  'Kuwait City': 'KWD', 'Salmiya': 'KWD', 'Hawalli': 'KWD', 'Fahaheel': 'KWD',
  'Manama': 'BHD', 'Riffa': 'BHD', 'Muharraq': 'BHD', 'Juffair': 'BHD',
  'Muscat': 'OMR', 'Salalah': 'OMR', 'Sohar': 'OMR', 'Nizwa': 'OMR',
}

export function getCurrency(city) {
  return CURRENCY_MAP[city] || 'AED'
}

export function formatMoney(amount, city) {
  if (!amount) return `${getCurrency(city)} 0`
  return `${getCurrency(city)} ${Number(amount).toLocaleString()}`
}

// ── Shared Styles (dynamic — respects dark mode via C tokens) ───────────
export function getInputStyle() {
  return {
    width: '100%', padding: '14px 16px', borderRadius: 12,
    border: `1px solid ${C.border}`, background: C.card,
    color: C.text, fontSize: 16, fontFamily: "'Outfit', sans-serif",
    boxSizing: 'border-box', outline: 'none', WebkitAppearance: 'none',
  }
}

export function getSelectStyle() {
  const base = getInputStyle()
  return {
    ...base, cursor: 'pointer',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238C8C8C' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center',
    paddingRight: 40,
  }
}

// Legacy exports for backward compat (static — use getInputStyle/getSelectStyle for dark mode)
export const inputStyle = {
  width: '100%', padding: '14px 16px', borderRadius: 12,
  border: `1px solid ${LIGHT.border}`, background: LIGHT.card,
  color: LIGHT.text, fontSize: 16, fontFamily: "'Outfit', sans-serif",
  boxSizing: 'border-box', outline: 'none', WebkitAppearance: 'none',
}

export const selectStyle = {
  ...inputStyle, cursor: 'pointer',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238C8C8C' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center',
  paddingRight: 40,
}

// ── Liquid Glass helper ──────────────────────────────────────────────────
// Returns an inline-style object for a frosted glass surface. Use instead
// of `background: C.card, border: 1px solid C.border` when the surface
// should pick up the aurora behind it.
export function glassStyle({ strong = false } = {}) {
  return {
    background: strong ? C.glassStrong : C.glass,
    border: `1px solid ${C.glassBorder}`,
    backdropFilter: 'blur(24px) saturate(160%)',
    WebkitBackdropFilter: 'blur(24px) saturate(160%)',
    boxShadow: C.glassShadow,
  }
}

// ── Global CSS ───────────────────────────────────────────────────────────
export const globalCSS = `
  /* Fonts loaded via <link> in index.html for faster rendering */
  * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
  body { margin: 0; font-family: 'Outfit', sans-serif; background: var(--bg); color: var(--text); }
  input[type="number"] { -moz-appearance: textfield; }
  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  select option { background: var(--card); color: var(--text); }
  .settle-scroll::-webkit-scrollbar { display: none; }
  .settle-scroll { scrollbar-width: none; -ms-overflow-style: none; -webkit-overflow-scrolling: touch; }
  .settle-card { transition: all 0.25s ease; cursor: pointer; }
  .settle-card:hover { transform: translateY(-3px); box-shadow: 0 8px 32px rgba(0,0,0,0.08); }
  .settle-card:active { transform: scale(0.98); }
  .settle-glass {
    background: var(--glass);
    border: 1px solid var(--glass-border);
    backdrop-filter: blur(24px) saturate(160%);
    -webkit-backdrop-filter: blur(24px) saturate(160%);
    transition: all 0.25s ease;
  }
  .settle-glass:hover { transform: translateY(-2px); }
  .settle-glass:active { transform: scale(0.98); }
`
